import { useState, useEffect, useCallback, useMemo } from 'react';
import type { Activity } from '../types';
import * as api from '../api';

export interface UseActivitiesParams {
  parentType?: 'lead' | 'opportunity';
  parentId?: string;
  limit?: number;
}

export interface GroupedActivities {
  overdue: Activity[];
  today: Activity[];
  upcoming: Activity[];
  completed: Activity[];
}

export interface UseActivitiesResult {
  activities: Activity[];
  grouped: GroupedActivities;
  loading: boolean;
  error: string | null;
  refreshData: () => Promise<void>;
  completeActivity: (activityId: string, outcome?: string) => Promise<boolean>;
  rescheduleActivity: (activityId: string, dueDate: string, reason?: string) => Promise<boolean>;
  createActivity: (activity: api.CreateActivityRequest) => Promise<Activity | null>;
}

function isToday(dateStr: string): boolean {
  const date = new Date(dateStr);
  const today = new Date();
  return (
    date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth() &&
    date.getDate() === today.getDate()
  );
}

function isOverdue(dateStr: string): boolean {
  const date = new Date(dateStr);
  const now = new Date();
  // Set to start of today for comparison
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return date < today;
}

function isFuture(dateStr: string): boolean {
  const date = new Date(dateStr);
  const now = new Date();
  // Set to end of today for comparison
  const todayEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
  return date > todayEnd;
}

export function useActivities({ parentType, parentId, limit }: UseActivitiesParams = {}): UseActivitiesResult {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.fetchActivities({
        parent_type: parentType,
        parent_id: parentId,
        limit: limit || 50,
      });

      setActivities(response.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load activities');
    } finally {
      setLoading(false);
    }
  }, [parentType, parentId, limit]);

  const completeActivity = useCallback(async (activityId: string, outcome?: string): Promise<boolean> => {
    // Optimistic update
    setActivities(prev =>
      prev.map(a =>
        a.id === activityId
          ? { ...a, status: 'completed' as const, completed_at: new Date().toISOString(), outcome_notes: outcome }
          : a
      )
    );

    try {
      await api.completeActivity(activityId, outcome ? { outcome } : undefined);
      return true;
    } catch (err) {
      // Rollback on error
      setActivities(prev =>
        prev.map(a =>
          a.id === activityId
            ? { ...a, status: 'scheduled' as const, completed_at: undefined, outcome_notes: undefined }
            : a
        )
      );
      setError(err instanceof Error ? err.message : 'Failed to complete activity');
      return false;
    }
  }, []);

  const rescheduleActivity = useCallback(async (
    activityId: string,
    dueDate: string,
    reason?: string
  ): Promise<boolean> => {
    const activity = activities.find(a => a.id === activityId);
    if (!activity) return false;

    const previousDueDate = activity.due_date;

    // Optimistic update
    setActivities(prev =>
      prev.map(a =>
        a.id === activityId
          ? { ...a, due_date: dueDate }
          : a
      )
    );

    try {
      await api.rescheduleActivity(activityId, { due_date: dueDate, reason });
      return true;
    } catch (err) {
      // Rollback on error
      setActivities(prev =>
        prev.map(a =>
          a.id === activityId
            ? { ...a, due_date: previousDueDate }
            : a
        )
      );
      setError(err instanceof Error ? err.message : 'Failed to reschedule activity');
      return false;
    }
  }, [activities]);

  const createActivity = useCallback(async (request: api.CreateActivityRequest): Promise<Activity | null> => {
    try {
      const newActivity = await api.createActivity(request);
      setActivities(prev => [newActivity, ...prev]);
      return newActivity;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create activity');
      return null;
    }
  }, []);

  // Group activities by status and due date
  const grouped = useMemo((): GroupedActivities => {
    const result: GroupedActivities = {
      overdue: [],
      today: [],
      upcoming: [],
      completed: [],
    };

    for (const activity of activities) {
      if (activity.status === 'completed') {
        result.completed.push(activity);
      } else if (activity.status === 'cancelled') {
        // Skip cancelled activities
        continue;
      } else if (activity.due_date) {
        if (isOverdue(activity.due_date)) {
          result.overdue.push(activity);
        } else if (isToday(activity.due_date)) {
          result.today.push(activity);
        } else if (isFuture(activity.due_date)) {
          result.upcoming.push(activity);
        } else {
          // No due date scheduled activities go to upcoming
          result.upcoming.push(activity);
        }
      } else {
        // Activities without due date go to upcoming
        result.upcoming.push(activity);
      }
    }

    // Sort each group by due_date
    const sortByDueDate = (a: Activity, b: Activity) => {
      if (!a.due_date && !b.due_date) return 0;
      if (!a.due_date) return 1;
      if (!b.due_date) return -1;
      return new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
    };

    const sortByCompletedAt = (a: Activity, b: Activity) => {
      if (!a.completed_at && !b.completed_at) return 0;
      if (!a.completed_at) return 1;
      if (!b.completed_at) return -1;
      return new Date(b.completed_at).getTime() - new Date(a.completed_at).getTime(); // Most recent first
    };

    result.overdue.sort(sortByDueDate);
    result.today.sort(sortByDueDate);
    result.upcoming.sort(sortByDueDate);
    result.completed.sort(sortByCompletedAt);

    return result;
  }, [activities]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  return {
    activities,
    grouped,
    loading,
    error,
    refreshData: loadData,
    completeActivity,
    rescheduleActivity,
    createActivity,
  };
}

// Utility functions for activity display
export function getActivityTypeIcon(type: Activity['type']): string {
  const icons: Record<Activity['type'], string> = {
    call: '📞',
    email: '✉️',
    meeting: '📅',
    task: '✅',
    note: '📝',
  };
  return icons[type] || '📋';
}

export function formatDueDate(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const targetDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  if (targetDate.getTime() === today.getTime()) {
    return 'Today';
  } else if (targetDate.getTime() === tomorrow.getTime()) {
    return 'Tomorrow';
  } else if (targetDate.getTime() === yesterday.getTime()) {
    return 'Yesterday';
  }

  // Within the next 7 days, show day name
  const diffDays = Math.ceil((targetDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays > 0 && diffDays <= 7) {
    return date.toLocaleDateString('en-US', { weekday: 'long' });
  }

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function formatDueTime(timeStr: string): string {
  const [hours, minutes] = timeStr.split(':').map(Number);
  const date = new Date();
  date.setHours(hours, minutes);
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}
